/** @internal – import from `@/store/useDepartmentStore` instead. */
import type { GrowthPipelineInput } from "../types";
import { runSSEStream } from "../sseRunner";
import { buildErrorMessage } from "../utils";
import { sseEvents } from "../../../events";

/**
 * Growth-research SSE pipeline runner. Posts to
 * `/core/coagent/growth-research` and publishes typed `growth:*`
 * events.
 *
 * Migrated off the `SSECallbacks` mutator-bag in Phase 8 commit 75.
 */
export async function runGrowthResearchSSE(
  growthInput: GrowthPipelineInput,
  sessionId: string,
  signal?: AbortSignal,
  googleIds?: Record<string, string>,
): Promise<void> {
  await runSSEStream(growthInput, {
    pipeline: "growth-research",
    endpoint: "/core/coagent/growth-research",
    sessionId,
    buildBody: (sid, input) => ({ sessionId: sid, growthInput: input, googleIds }),
    errorPrefix: "Growth research",
    signal,
    debugMeta: { businessName: growthInput.businessName, hasAbortSignal: Boolean(signal) },
    handleEvent: (event) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { phase, status, data, error, details } = event as Record<string, any>;
      const ev = data as Record<string, unknown> | undefined;

      if (phase === "session_named" && ev?.name) {
        sseEvents.publish({
          type: "growth:session-named",
          pipeline: "growth-research",
          sessionId,
          name: ev.name as string,
        });
        return;
      }

      if (phase === "website_scrape" && status === "running") {
        sseEvents.publish({
          type: "growth:website-scrape-running",
          pipeline: "growth-research",
          sessionId,
          url: typeof ev?.url === "string" ? ev.url : "",
        });
        return;
      }

      if (phase === "website_scrape" && status === "complete") {
        sseEvents.publish({
          type: "growth:website-scrape-complete",
          pipeline: "growth-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "website_scrape" && status === "skipped") {
        sseEvents.publish({
          type: "growth:website-scrape-skipped",
          pipeline: "growth-research",
          sessionId,
          reason: typeof ev?.reason === "string" ? ev.reason : "unknown",
        });
        return;
      }

      if (phase === "site_analysis" && status === "running") {
        sseEvents.publish({
          type: "growth:site-analysis-running",
          pipeline: "growth-research",
          sessionId,
        });
        return;
      }

      if (phase === "site_analysis" && status === "complete") {
        sseEvents.publish({
          type: "growth:site-analysis-complete",
          pipeline: "growth-research",
          sessionId,
          data: ev ?? {},
          inputUrl: growthInput.url,
        });
        return;
      }

      if (phase === "event_extraction" && status === "complete") {
        sseEvents.publish({
          type: "growth:event-extraction-complete",
          pipeline: "growth-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "done") {
        sseEvents.publish({
          type: "growth:done",
          pipeline: "growth-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "error") {
        const message = buildErrorMessage(error, details, "Growth research pipeline failed");
        console.error("[coagent/growth-research] SSE error", { sessionId, message, details });
        sseEvents.publish({
          type: "growth:error",
          pipeline: "growth-research",
          sessionId,
          message,
          ...(details !== undefined ? { details } : {}),
        });
      }
    },
  });
}
